import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, AlertTriangle, Timer } from "lucide-react";
import { fmtNum, fmtSigned, apiError } from "./balanceFields";

const OvertimeBreakdown = ({ row, year }) => (
  <div className="flex items-start gap-2 bg-[#16345c]/5 border border-[#16345c]/15 rounded-sm px-3 py-2 text-[12.5px] text-gray-700" data-testid="balance-edit-ot-breakdown">
    <Timer size={15} className="text-[#16345c] mt-0.5 shrink-0" />
    <div>
      Da timbrature {year}: <b>{fmtNum(row.overtime_calc)} h</b> · Rettifica attuale: <b>{fmtSigned(row.overtime_adjust)} h</b>
      <div className="text-[11.5px] text-gray-500 mt-0.5">Inserisci il nuovo totale: la rettifica sarà ricalcolata come differenza rispetto alle ore da timbrature.</div>
    </div>
  </div>
);

const EditStep = ({ row, col, year, value, setValue, reason, setReason }) => (
  <div className="space-y-4">
    {col.key === "overtime" && <OvertimeBreakdown row={row} year={year} />}
    <div>
      <label className="block text-[12px] font-bold text-gray-600 mb-1">Valore attuale</label>
      <div className="text-[22px] font-black text-[#16345c] tabular-nums" data-testid="balance-edit-current">{fmtNum(row[col.key])} {col.unit}</div>
    </div>
    <div>
      <label className="block text-[12px] font-bold text-gray-600 mb-1">Nuovo valore ({col.unit})</label>
      <input type="text" inputMode="decimal" value={value} autoFocus onChange={(e) => setValue(e.target.value)} data-testid="balance-edit-value"
        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[15px] font-bold tabular-nums outline-none focus:border-[#16345c]" />
    </div>
    <div>
      <label className="block text-[12px] font-bold text-gray-600 mb-1">Motivazione <span className="text-[#e30613]">*</span></label>
      <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="balance-edit-reason"
        placeholder="es. Allineamento con cedolino di maggio" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[13.5px] outline-none focus:border-[#16345c] resize-none" />
    </div>
  </div>
);

const ConfirmStep = ({ row, col, before, after, reason }) => (
  <div className="space-y-3" data-testid="balance-edit-confirm-step">
    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-[12.5px] px-3 py-2 rounded-sm">
      <AlertTriangle size={15} className="mt-0.5 shrink-0" /> La modifica verrà applicata subito e registrata nello storico con il tuo nome.
    </div>
    <div className="text-[13px] text-gray-600"><b className="text-[#16345c]">{row.name}</b> · {col.label}</div>
    <div className="flex items-center gap-3 text-[20px] font-black tabular-nums" data-testid="balance-edit-summary">
      <span className="text-gray-400 line-through">{fmtNum(before)}</span>
      <ArrowRight size={18} className="text-gray-400" />
      <span className="text-[#16345c]">{fmtNum(after)} {col.unit}</span>
      <span className={`text-[12px] font-bold px-2 py-0.5 rounded-full ${after - before >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{fmtSigned(Math.round((after - before) * 100) / 100)}</span>
    </div>
    <div className="text-[12.5px] text-gray-600 bg-gray-50 border border-gray-100 rounded-sm px-3 py-2"><b>Motivo:</b> {reason}</div>
  </div>
);

export const BalanceEditDialog = ({ target, year, onClose, onSaved }) => {
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [step, setStep] = useState("edit");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) { setValue(String(target.row[target.col.key] ?? 0).replace(".", ",")); setReason(""); setStep("edit"); }
  }, [target]);

  if (!target) return null;
  const { row, col } = target;
  const before = Number(row[col.key] || 0);
  const parsed = Number(String(value).trim().replace(",", "."));

  const next = () => {
    if (String(value).trim() === "" || Number.isNaN(parsed)) return toast.error("Inserisci un valore numerico valido");
    if (Math.round(parsed * 100) === Math.round(before * 100)) return toast.error("Il nuovo valore è uguale a quello attuale");
    if (reason.trim().length < 3) return toast.error("La motivazione è obbligatoria (min. 3 caratteri)");
    setStep("confirm");
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/balances/${row.id}`, { field: col.key, value: parsed, reason: reason.trim(), year });
      toast.success(`${col.label} aggiornato per ${row.name}`);
      onSaved(res.data.row);
    } catch (e) {
      toast.error(apiError(e, "Errore nel salvataggio"));
      setStep("edit");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-[460px]" data-testid="balance-edit-dialog">
        <DialogHeader>
          <DialogTitle>{step === "edit" ? `Modifica ${col.label}` : "Confermi la modifica?"}</DialogTitle>
          <DialogDescription className="text-[13px]">{row.name}{row.matricola ? ` · ${row.matricola}` : ""}{col.key === "overtime" ? ` · anno ${year}` : ""}</DialogDescription>
        </DialogHeader>
        {step === "edit"
          ? <EditStep row={row} col={col} year={year} value={value} setValue={setValue} reason={reason} setReason={setReason} />
          : <ConfirmStep row={row} col={col} before={before} after={parsed} reason={reason.trim()} />}
        <DialogFooter>
          <button onClick={step === "edit" ? onClose : () => setStep("edit")} disabled={saving} data-testid="balance-edit-cancel"
            className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">{step === "edit" ? "Annulla" : "Indietro"}</button>
          {step === "edit"
            ? <button onClick={next} data-testid="balance-edit-next" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors">Continua</button>
            : <button onClick={save} disabled={saving} data-testid="balance-edit-confirm" className="px-4 py-2 text-[13px] font-bold text-white bg-[#009640] hover:bg-[#007a34] rounded-sm transition-colors disabled:opacity-60">{saving ? "Salvataggio..." : "Conferma modifica"}</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
